import cytoscape, {
  Collection,
  Core,
  EdgeSingular,
  NodeSingular,
  Selector,
  SingularElementArgument,
} from "cytoscape";
import { NodeType, RuleType } from "fe-common/graphql";

export enum CustomGraphData {
  hyper = "hyper",
  origEdges = "origEdges",
  origNodes = "origNodes",
  origSource = "origSource",
  origTarget = "origTarget",
  subGraphForward = "subGraphForward",
}

// DATA ELEMENTS stored in graph nodes
export enum DataElement {
  shown = "shown",
  weight = "weight",
  label = "label",
  shortpath = "shortpath",
}

export enum CustomScratchData {
  subGraph = "subGraph",
}

const eleInCollection = (
  theCollection: Collection,
  e: NodeSingular | EdgeSingular,
) => e != null && theCollection.getElementById(e.id()).nonempty();
const validEle = <T extends NodeSingular | EdgeSingular>(
  theCollection: Collection,
  e: T,
) => (eleInCollection(theCollection, e) ? e : null);

export enum GraphSet {
  include = "include",
  exclude = "exclude",
}

function replaceEdge(
  collection: Collection,
  edge: EdgeSingular,
  forward: boolean,
) {
  let cy = edge && edge.cy();
  let theCollection = { ref: collection };
  const currentCollection = () => theCollection.ref;
  const inCollection = (e) => eleInCollection(currentCollection(), e);
  const valid = (e) => validEle(currentCollection(), e);
  const weight = (e: NodeSingular | EdgeSingular) =>
    e == null || e.data("ISA") ? 1 : e.data(DataElement.weight);
  const addToSubGraph = (
    parent: EdgeSingular,
    e: NodeSingular | EdgeSingular,
  ) => {
    let scratch = parent.scratch(CustomScratchData.subGraph);
    if (!scratch) scratch = cy.collection();
    parent.scratch(CustomScratchData.subGraph, scratch.union(e));
  };
  const addToCollection = (e) =>
    (theCollection.ref = theCollection.ref.union(e));
  const removeFromCollection = (e) =>
    (theCollection.ref = theCollection.ref.not(e));

  let source = valid(edge.source());
  let target = valid(edge.target());

  let nodeToBypass = forward ? target : source;

  let nodeToExtend = forward ? source : target;

  // cant remove edge nodes
  let isEdgeNode =
    nodeToBypass &&
    (nodeToBypass.data("type") == NodeType.Disease ||
      nodeToBypass.data("type") == NodeType.Finding);

  if (nodeToBypass && !isEdgeNode && nodeToExtend) {
    let edgesToExtend = forward
      ? nodeToBypass
          .outgoers()
          .edges()
          .filter(inCollection)
          .filter((e: EdgeSingular) => inCollection(e.target()))
      : nodeToBypass
          .incomers()
          .edges()
          .filter(inCollection)
          .filter((e: EdgeSingular) => inCollection(e.source()));

    let extendedEdges = [];
    edgesToExtend &&
      edgesToExtend.forEach((e) => {
        let etarget = forward ? e.target() : target;
        let esource = forward ? source : e.source();

        // skip adding the same edge twice
        if (etarget.id() == e.target().id() && esource.id() == e.source().id())
          return;

        // does the collection already contain a hyperedge for this edge?
        let hyperEdge = currentCollection().edges(
          `edge[source = "${esource.id()}"][target = "${etarget.id()}"][?hyper]`,
        );
        let extended = null;
        if (hyperEdge && hyperEdge.nonempty()) {
          extended = hyperEdge.first();
          // adjust hyperedge
          let origEdges = extended.data(CustomGraphData.origEdges) || [];
          origEdges.push(e.id());
          extended.data(CustomGraphData.origEdges, origEdges);
          // compute weight adjustment
          extended.data(
            DataElement.weight,
            weight(extended) + weight(extended) * weight(edge),
          );
        } else {
          // create new hyperedge
          // extend edge from source;
          let data = e.json();

          // fix id + add data
          data.data.id = `hyper-${e.id()}-${edge.id()}`;
          data.data.edgeid = data.data.id;
          data.data[CustomGraphData.hyper] = true;
          data.data[CustomGraphData.origEdges] = [e.id()];

          // fix source or target
          if (forward) {
            data.data[CustomGraphData.origSource] = e.source().id();
            data.data.source = esource.id();
            data.data[CustomGraphData.subGraphForward] = true;
          } else {
            data.data[CustomGraphData.origTarget] = e.target().id();
            data.data.target = etarget.id();
            data.data[CustomGraphData.subGraphForward] = false;
          }

          // adjust weight
          data.data["weight"] = weight(e) * weight(edge);

          // create the edge
          extended = cy.add(data);

          // start visible
          extended.style("display", "element");

          // add to graph
          addToCollection(extended);

          extended.data("id", "hyper_" + e.id());
          extended.data(CustomGraphData.hyper, true);
          extended.data(CustomGraphData.origEdges, [e.id()]);
          // compute weight adjustment
          extended.data(DataElement.weight, weight(e) * weight(edge));
        }
        extendedEdges.push(extended);
      });

    // remove old edge
    removeFromCollection(edge);
    extendedEdges.forEach((p) => addToSubGraph(p, edge));
    // possibly remove node
    if (inCollection(nodeToBypass)) {
      let node = nodeToBypass;
      let remainingEdges = (forward ? node.incomers() : node.outgoers())
        .edges()
        .filter(inCollection);
      if (!remainingEdges || remainingEdges.length == 0) {
        removeFromCollection(
          forward ? node.outgoers().edges() : node.incomers().edges(),
        );
        removeFromCollection(node);
        extendedEdges.forEach((p) => addToSubGraph(p, node));
      }
    }
    return {
      removed: true,
      elements: currentCollection(),
    };
  } else {
    // no success
    return {
      removed: false,
      elements: collection,
    };
  }
}

function collapseEdges(
  startingCollection: Collection,
  selector: Selector,
): Collection {
  let theCollection = startingCollection;
  let failedToRemove = null;
  while (true) {
    // find a candidate to remove
    let candidates = failedToRemove
      ? theCollection.not(failedToRemove)
      : theCollection;
    let edge = candidates.edges(selector);
    if (!edge || edge.empty()) {
      break;
    }
    let oneEdge = edge.first();
    let { removed, elements } = replaceEdge(theCollection, oneEdge, true);
    if (!removed) {
      // try again the other direction
      let otherDirection = replaceEdge(theCollection, oneEdge, false);
      removed = otherDirection.removed;
      elements = otherDirection.elements;
    }
    if (removed) {
      theCollection = elements;
      failedToRemove = null;
    } else {
      failedToRemove = failedToRemove ? failedToRemove.union(oneEdge) : oneEdge;
    }
  }
  return theCollection;
}
export function markCollapsedIsA(cy: Core, marker: string) {
  let selector = `edge[?${RuleType.Isa}]`;
  let start = cy.elements();
  let visible = collapseEdges(start, selector);
  let { left, right, both } = start.diff(visible);
  cy.elements()
    .intersection(left)
    .data(marker, GraphSet.exclude);
  cy.elements()
    .intersection(right)
    .data(marker, GraphSet.include);
  // console.table(left.map(l=>({label: l.data("label"), marker: l.data(marker)})));
  // console.table(right.map(l=>({label: l.data("label"), marker: l.data(marker)})));
  // console.table(both.map(l=>({label: l.data("label"), marker: l.data(marker)})));
}

export function reduceVisibleNodes(
  cy: cytoscape.Core,
  max: number,
  marker: string,
  isVisible: (e) => boolean,
): number {
  let changed = false;
  let truncatedNodes = 0;
  if (cy) {
    cy.startBatch();
    cy.nodes().removeData(marker);
    let start = cy.nodes('[type = "INNER"]').filter(isVisible);
    let visible = start;
    if (start.length > max) {
      visible = start.sort(
        (a, b) => a.data(DataElement.weight) - b.data(DataElement.weight),
      );
      visible = visible.slice(0, start.length - max);
      visible.data(marker, GraphSet.include);
      changed = true;
      truncatedNodes = start.length - max;
    } else {
      truncatedNodes = 0;
    }
    cy.endBatch();
  }
  return truncatedNodes;
}

export function shortestPath(
  all: Collection,
  start: Collection | SingularElementArgument,
  end: Collection | SingularElementArgument,
) {
  // let path = all.aStar({
  //   root: start,
  //   goal: end,
  //   weight: edge => {
  //   	 let w = edge.first().data(DataElement.weight);
  //   	 let isa = edge.first().data("ISA");
  //   	 return isa? 0: (1.0 - w);
  // 	},
  //   directed: true
  // });
  // look for direct connection first
  let direct = start.nodes().edgesTo(end.nodes());
  if (direct.nonempty()) {
    return direct.edges().max((e) => e.data(DataElement.weight)).ele;
  } else {
    return null;
  }
  // else run a shortest path
  // let d = all.dijkstra({
  // 	root: start,
  // 	directed: true,
  // 	weight: edge => {
  // 		 let w = edge.first().data(DataElement.weight);
  // 		 let isa = edge.first().data("ISA");
  // 		 return isa? 0.01: Math.log10(1.0 - w);
  // 	}
  // });
  // let path = d.pathTo(end.nodes().first());
  // return path;
}

export function filterUnreachableElements(
  cy: cytoscape.Core,
  isVisible: (e) => boolean,
) {
  let start = Date.now();
  let shown = cy.elements().filter(isVisible);
  let root = shown.filter(`node[type = "${NodeType.Disease}"]`);
  let findings = shown.filter(`node[type = "${NodeType.Finding}"]`);
  let inner = shown
    .nodes()
    .not(findings)
    .not(root);

  let hasAnyPath = (
    a: Collection | SingularElementArgument,
    b: Collection | SingularElementArgument,
  ) => {
    return a.some((start) =>
      b.some(
        (end) =>
          shown.aStar({ root: start, goal: end, directed: true }).found ||
          shown.aStar({ root: end, goal: start, directed: true }).found,
      ),
    );
  };

  // remove unconnected inner nodes
  let connectedInner = inner.filter((e) => {
    // we need to connect to the root - one way or the other
    if (!hasAnyPath(root, e)) return false;
    // we need to connect to a symptom
    if (!hasAnyPath(e, findings)) return false;
    return true;
  });
  inner.not(connectedInner).data(DataElement.shown, false);
}
